import type { ObjectOptions } from 'api/requestUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export const getOptions = (accessToken?: string): ObjectOptions =>
  accessToken ?
      { headers: { Authorization: `Bearer ${accessToken}` } } :
      { credentials: 'include' }

export const baseUri = '/api/trips'
export const baseTemplatesUri = `${baseUri}/templates`

// Max length for user-friendly error messages (longer messages are likely technical)
const MAX_USER_FRIENDLY_MESSAGE_LENGTH = 200

function getDefaultErrorMessages(): Record<number, string> {
  const intl = getCurrentIntl()
  return {
    400: intl.formatMessage({
      id: 'KI_7IqniM-mAPFDdJ-mD',
      defaultMessage: 'Invalid request. Please check your input and try again.',
    }),
    401: intl.formatMessage({
      id: 'JVVfKUwiqnjbWmWbsdsx',
      defaultMessage: 'Please sign in to continue.',
    }),
    403: intl.formatMessage({
      id: '-DWpbEmob0dAryZs6WGc',
      defaultMessage: 'You don’t have permission to perform this action.',
    }),
    404: intl.formatMessage({
      id: 'Bq6JUdUDu_xrM3L3_NuL',
      defaultMessage: 'The requested resource was not found.',
    }),
    422: intl.formatMessage({
      id: 'VCm7zoE6_6YM1hYcY-xJ',
      defaultMessage: 'Unable to process your request. Please try again.',
    }),
    500: intl.formatMessage({
      id: 'wuxv-RZKs8X-kL16f6xo',
      defaultMessage: 'Something went wrong on our end. Please try again later.',
    }),
    502: intl.formatMessage({
      id: 'YkaYPRhh-MFLwKs_k_7F',
      defaultMessage: 'Service temporarily unavailable. Please try again later.',
    }),
    503: intl.formatMessage({
      id: 'XZI8Vl98H2dAgc_7j-b-',
      defaultMessage: 'Service temporarily unavailable. Please try again later.',
    }),
  }
}

/**
 * Extract a user-friendly error message from an API error response.
 * Follows the codebase's extractError pattern from reducers.
 *
 * API error structure: { status, message, errors: [{ message, path, value }] }
 */
export function extractApiErrorMessage(errorText: string, statusCode: number): string {
  const defaultMessages = getDefaultErrorMessages()
  const intl = getCurrentIntl()

  const fallbackMessage = defaultMessages[statusCode] ?? intl.formatMessage({
    id: 'mvy4pN-y5NdfT5_2lohE',
    defaultMessage: 'Something went wrong. Please try again.',
  })

  if (!errorText) {
    return fallbackMessage
  }

  try {
    const parsed = JSON.parse(errorText)

    // Handle errors array (Strummer validation errors)
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
      const firstError = parsed.errors[0]
      if (firstError.message) {
        // For validation errors, show the specific field error
        // e.g., "userMessageId should be a UUID" -> just show generic message
        // as field names are technical and not user-friendly
        return fallbackMessage
      }
    }

    // Use the top-level message if available and user-friendly
    if (parsed.message && typeof parsed.message === 'string') {
      // Check if message looks technical (contains JSON, code terms, etc.)
      const technicalPatterns = ['{', '[', 'undefined', 'null', 'NaN', 'Error:']
      const isTechnical = technicalPatterns.some(p => parsed.message.includes(p))
      if (!isTechnical && parsed.message.length < MAX_USER_FRIENDLY_MESSAGE_LENGTH) {
        return parsed.message
      }
    }

    return fallbackMessage
  } catch {
    // Not valid JSON - check if it's a clean text message
    if (errorText.length < MAX_USER_FRIENDLY_MESSAGE_LENGTH && !errorText.includes('{') && !errorText.includes('<')) {
      return errorText
    }
    return fallbackMessage
  }
}
